/**
 * Materials / Tools Step - Equipment inventory
 *
 * New step for tracking tools and materials.
 * Uses existing `tool` entity in YAML schema.
 */

import React, { useState } from 'react';
import { Wrench, Plus, Trash2, Check, Package, Euro, MapPin } from 'lucide-react';

const TOOL_CATEGORIES = [
  { value: 'outillage', label: 'Outillage' },
  { value: 'epi', label: 'EPI (sécurité)' },
  { value: 'materiel_roulant', label: 'Matériel roulant' },
  { value: 'bureautique', label: 'Bureautique / IT' },
  { value: 'autre', label: 'Autre' },
];

export function MaterialsStep({ data, updateData, onNext, onSkip }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMaterial, setNewMaterial] = useState({
    name: '',
    category: 'outillage',
    location: '',
    value: '',
  });

  const hasMaterials = data.hasMaterials;
  const materials = data.materials || [];

  // Build location options from sites + vehicles
  const locationOptions = [
    ...(data.sites || []).map((s, i) => ({ value: `site:${i}`, label: `Site: ${s.name}` })),
    ...(data.vehicles || [])
      .filter(v => v.license_plate || v.make)
      .map((v, i) => ({ value: `vehicle:${i}`, label: `Véh: ${v.license_plate || `${v.make} ${v.model}`}` })),
  ];

  const handleChoiceChange = (choice) => {
    updateData({ hasMaterials: choice });
    if (choice === false || choice === 'later') {
      // Skip, go next
    }
  };

  const addMaterial = () => {
    if (!newMaterial.name.trim()) return;

    const updated = [...materials, {
      name: newMaterial.name.trim(),
      category: newMaterial.category,
      location: newMaterial.location,
      value: newMaterial.value ? parseFloat(newMaterial.value) : null,
    }];
    updateData({ materials: updated });
    setNewMaterial({ name: '', category: 'outillage', location: '', value: '' });
    setShowAddForm(false);
  };

  const removeMaterial = (index) => {
    updateData({ materials: materials.filter((_, i) => i !== index) });
  };

  // If user hasn't decided yet, show choice cards
  if (hasMaterials === null || hasMaterials === undefined) {
    return (
      <div className="space-y-8 animate-fade-in">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl">
            <Wrench className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            Avez-vous du matériel à suivre ?
          </h2>
          <p className="text-gray-500">
            Outillage, EPI, équipements stockés dans vos véhicules ou sur site
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <button
            onClick={() => handleChoiceChange(true)}
            className="p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-amber-400 hover:bg-amber-50 transition-all text-center"
          >
            <Package className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <span className="text-gray-900 font-medium block">Oui</span>
            <span className="text-gray-400 text-xs mt-1 block">J'ai du matériel</span>
          </button>
          <button
            onClick={() => { handleChoiceChange(false); onSkip(); }}
            className="p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-300 transition-all text-center"
          >
            <Wrench className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <span className="text-gray-900 font-medium block">Non</span>
            <span className="text-gray-400 text-xs mt-1 block">Pas de suivi matériel</span>
          </button>
          <button
            onClick={() => { handleChoiceChange('later'); onSkip(); }}
            className="p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-300 transition-all text-center"
          >
            <span className="text-2xl mb-1 block">🕐</span>
            <span className="text-gray-900 font-medium block">Plus tard</span>
            <span className="text-gray-400 text-xs mt-1 block">Compléter après</span>
          </button>
        </div>
      </div>
    );
  }

  // Materials entry mode
  return (
    <div className="space-y-8 animate-fade-in">
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl">
          <Wrench className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          Inventaire matériel
        </h2>
        <p className="text-gray-500">
          Ajoutez vos outils et équipements
        </p>
      </div>

      {/* Materials list */}
      {materials.length > 0 && (
        <div className="space-y-2">
          {materials.map((mat, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl shadow-sm"
            >
              <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
                <Package className="w-4 h-4 text-orange-500" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-gray-900 font-medium truncate block">{mat.name}</span>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <span>{TOOL_CATEGORIES.find(c => c.value === mat.category)?.label || mat.category}</span>
                  {mat.location && <span>| {mat.location}</span>}
                  {mat.value && <span>| {mat.value} €</span>}
                </div>
              </div>
              <button
                onClick={() => removeMaterial(index)}
                className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      {showAddForm ? (
        <div className="p-4 bg-white border border-gray-300 rounded-xl space-y-4 shadow-sm animate-fade-in">
          <h4 className="text-gray-900 font-medium">Nouveau matériel</h4>

          {/* Name */}
          <input
            type="text"
            value={newMaterial.name}
            onChange={(e) => setNewMaterial(prev => ({ ...prev, name: e.target.value }))}
            placeholder="ex: Perceuse Hilti, Groupe électrogène..."
            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            autoFocus
          />

          <div className="grid grid-cols-2 gap-3">
            {/* Category */}
            <div className="space-y-1">
              <label className="text-gray-600 text-xs font-medium">Catégorie</label>
              <select
                value={newMaterial.category}
                onChange={(e) => setNewMaterial(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                {TOOL_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>

            {/* Value */}
            <div className="space-y-1">
              <label className="text-gray-600 text-xs font-medium flex items-center gap-1">
                <Euro className="w-3 h-3" />
                Valeur estimée
              </label>
              <input
                type="number"
                value={newMaterial.value}
                onChange={(e) => setNewMaterial(prev => ({ ...prev, value: e.target.value }))}
                placeholder="Optionnel"
                className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Location */}
          {locationOptions.length > 0 && (
            <div className="space-y-1">
              <label className="text-gray-600 text-xs font-medium flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Emplacement
              </label>
              <select
                value={newMaterial.location}
                onChange={(e) => setNewMaterial(prev => ({ ...prev, location: e.target.value }))}
                className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="">Non spécifié</option>
                {locationOptions.map(loc => (
                  <option key={loc.value} value={loc.value}>{loc.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setShowAddForm(false)}
              className="flex-1 px-4 py-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={addMaterial}
              disabled={!newMaterial.name.trim()}
              className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Check className="w-4 h-4" />
              Ajouter
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-400 hover:text-gray-700 hover:border-gray-400 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Ajouter du matériel</span>
        </button>
      )}

      {/* Summary */}
      {materials.length > 0 && (
        <div className="text-center">
          <span className="px-4 py-2 bg-orange-50 border border-orange-200 rounded-full text-sm text-orange-700 font-medium">
            {materials.length} matériel{materials.length > 1 ? 's' : ''} ajouté{materials.length > 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Skip hint */}
      <div className="text-center">
        <button
          onClick={onSkip}
          className="text-gray-400 hover:text-gray-600 text-sm underline transition-colors"
        >
          Compléter plus tard
        </button>
      </div>
    </div>
  );
}

export default MaterialsStep;
